import type { ReactElement } from 'react';
import type { LeadQueueIndicatorState } from '@/presentation/hooks/useLeadQueueSync';
import styles from './OnlineStatusIndicator.module.css';

export interface OnlineStatusIndicatorProps {
  state: LeadQueueIndicatorState;
  /** Contagem de leads pendentes, usada no `aria-label` durante a
   * sincronização (Design > Indicador de Status: "Sincronizando X leads"). */
  pendingCount: number;
}

const LABELS: Record<LeadQueueIndicatorState, string> = {
  online: 'Online',
  offline: 'Offline',
  syncing: 'Sincronizando',
  stuck: 'Falha',
};

function buildAriaLabel(state: LeadQueueIndicatorState, pendingCount: number): string {
  if (state === 'syncing') return `Sincronizando ${pendingCount} leads`;
  // LOCAL-002: "stuck" — falha permanente de sincronização (409/422, gate de
  // consentimento/versão). O rótulo curto ("Falha") já cobre o breakpoint
  // ≥1024px; o `aria-label` carrega a orientação de ação para quem usa
  // leitor de tela.
  if (state === 'stuck') return 'Falha — verifique consentimento';
  return LABELS[state];
}

/**
 * Indica visualmente o status de sincronização da fila de leads: 🟢 online,
 * 🔴 offline, 🔄 sincronizando ou 🟠 falha permanente ("stuck", LOCAL-002) —
 * evolução da Fatia 1 (HUB-236, CA-07) e da Fatia 2 (LOCAL-002).
 * `state`/`pendingCount` vêm de `useLeadQueueSync`, chamado uma única vez
 * (em `useActivationFlow`) para não criar múltiplos pollers/registros de
 * Background Sync concorrentes.
 *
 * Acessível: `role="status"` + `aria-live="polite"` (não "alert", para não
 * interromper agressivamente a cada pequena mudança de estado).
 */
export function OnlineStatusIndicator({
  state,
  pendingCount,
}: OnlineStatusIndicatorProps): ReactElement {
  return (
    <div
      className={`${styles.container} ${styles[state]}`}
      role="status"
      aria-live="polite"
      aria-label={buildAriaLabel(state, pendingCount)}
    >
      <span className={styles.dot} aria-hidden="true" />
      <span className={styles.label}>{LABELS[state]}</span>
    </div>
  );
}
